import { useEffect, useRef, useState } from 'react'

import { useUserData } from '../../../../hooks'
import {
  GRETA_CHAT_LIST_HOVER_CLOSE_DELAY,
  GRETA_PANEL_CHATLIST_WIDTH,
  GretaAIChatMode,
  GretaAIChatPanelPrefKey,
  GretaAIChatPanelView,
} from '../../GretaAIChat.constants'

import {
  getToggledGretaAIChatMode,
  readGretaAIChatPanelPrefs,
  writeGretaAIChatPanelPref,
} from './useGretaAIChatPanelState.utils'

export function useGretaAIChatPanelState() {
  const { directory_id: userId } = useUserData()

  const [storedPrefs] = useState(() => readGretaAIChatPanelPrefs(userId))

  const [isOpen, setIsOpen] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [mode, setMode] = useState(GretaAIChatMode.Compact)
  const [view, setView] = useState(GretaAIChatPanelView.Chat)
  const [activeChatId, setActiveChatId] = useState(
    storedPrefs[GretaAIChatPanelPrefKey.activeChatId] ?? null
  )
  const [isChatListColumnOpen, setIsChatListColumnOpen] = useState(true)
  const [isChatListOrderFrozen, setIsChatListOrderFrozen] = useState(false)
  const [isChatListPinned, setIsChatListPinned] = useState(false)
  const [isChatListHovered, setIsChatListHovered] = useState(false)
  const [chatListWidth, setChatListWidth] = useState(
    storedPrefs[GretaAIChatPanelPrefKey.chatListWidth] ??
      GRETA_PANEL_CHATLIST_WIDTH
  )

  const hoverCloseTimerRef = useRef(null)

  useEffect(() => () => clearTimeout(hoverCloseTimerRef.current), [])

  useEffect(() => {
    writeGretaAIChatPanelPref(
      userId,
      GretaAIChatPanelPrefKey.activeChatId,
      activeChatId
    )
  }, [activeChatId, userId])

  useEffect(() => {
    writeGretaAIChatPanelPref(
      userId,
      GretaAIChatPanelPrefKey.chatListWidth,
      chatListWidth
    )
  }, [chatListWidth, userId])

  function handleOpen() {
    setIsOpen(true)
    setIsMinimized(false)
  }

  function handleMinimize() {
    setIsMinimized(true)
  }

  function handleToggleMode() {
    setMode(getToggledGretaAIChatMode)
  }

  function toggleChatListColumn() {
    setIsChatListColumnOpen(current => !current)
  }

  function pinChatList() {
    setIsChatListPinned(true)
  }

  // Hover opens a transient preview; the close delay bridges the gap between
  // the toggle and the drawer so moving between them keeps it open.
  function hoverChatList() {
    clearTimeout(hoverCloseTimerRef.current)
    setIsChatListHovered(true)
  }

  function unhoverChatList() {
    clearTimeout(hoverCloseTimerRef.current)
    hoverCloseTimerRef.current = setTimeout(
      () => setIsChatListHovered(false),
      GRETA_CHAT_LIST_HOVER_CLOSE_DELAY
    )
  }

  function closeChatList() {
    clearTimeout(hoverCloseTimerRef.current)
    setIsChatListPinned(false)
    setIsChatListHovered(false)
  }

  function openChat(chatId) {
    setView(GretaAIChatPanelView.Chat)
    setActiveChatId(chatId)
  }

  function openNewChat() {
    openChat(null)
  }

  function openProjectsPage() {
    closeChatList()
    setView(GretaAIChatPanelView.Projects)
    setActiveChatId(null)
  }

  return {
    activeChatId,
    chatListWidth,
    closeChatList,
    handleMinimize,
    handleOpen,
    handleToggleMode,
    hoverChatList,
    isChatListColumnOpen,
    isChatListHovered,
    isChatListOrderFrozen,
    isChatListPinned,
    isMinimized,
    isOpen,
    mode,
    openChat,
    openNewChat,
    openProjectsPage,
    pinChatList,
    setActiveChatId,
    setChatListWidth,
    setIsChatListOrderFrozen,
    toggleChatListColumn,
    unhoverChatList,
    view,
  }
}
